import { useServerLog } from '@/api'
import { resourceKeys } from '@/api/hooks/keys'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { shouldRefetchStaleVisits } from './visitSelfHeal'

/** How often to check visit freshness while mounted (local work only). */
const SAMPLE_INTERVAL_MS = 5_000
/** Visit-list data older than this (ms) while visible triggers a forced refetch. */
const STALE_MS = 30_000
/**
 * Minimum gap between forced refetches. Collapses the resume-event burst
 * (visibilitychange + resume + focus fire within ms of one wake) and the
 * steady-state interval to at most one refetch per window, so a slow link is not
 * hammered with overlapping visit-list refetches.
 */
const REFETCH_THROTTLE_MS = 15_000

const DOCUMENT_EVENTS = ['visibilitychange', 'resume'] as const
const WINDOW_EVENTS = ['pageshow', 'focus', 'online'] as const

const currentVisibility = () =>
  typeof document === 'undefined' ? 'unknown' : document.visibilityState

/** All active `ServiceVisit` by-source list queries, across every calendar view. */
const visitScopeKey = () => resourceKeys('ServiceVisit').bySourceScope()

export type HealStaleVisitsParams = {
  visible: boolean
  now: number
  lastRefetchAt: number
  staleMs: number
  throttleMs: number
}

export type HealStaleVisitsResult = {
  /** Whether a refetch was issued. */
  refetched: boolean
  /** Age (ms) of the freshest active visit data at decision time (0 if none loaded). */
  ageMs: number
  /** Number of active visit-list queries considered (and refetched, if any). */
  queryCount: number
}

/**
 * Force-refetch the active visit-list queries when they are stale. `refetchQueries`
 * is used deliberately: it ignores `staleTime`, so it actually runs under the
 * mobile `staleTime: Infinity` default where focus/reconnect refetch would be a
 * no-op. Returns the stale age and query count so the caller can log a useful
 * telemetry breadcrumb. `onRefetch` is invoked synchronously the instant a
 * refetch is decided — before the (potentially slow) refetch resolves — so the
 * caller can mark its throttle at issue time and not re-issue overlapping
 * refetches while the first is in flight. Exported for testing.
 */
export const healStaleVisits = async (
  queryClient: QueryClient,
  { visible, now, lastRefetchAt, staleMs, throttleMs }: HealStaleVisitsParams,
  onRefetch?: () => void
): Promise<HealStaleVisitsResult> => {
  const active = queryClient
    .getQueryCache()
    .findAll({ queryKey: visitScopeKey(), type: 'active' })
  const newest = active.reduce(
    (max, q) => Math.max(max, q.state.dataUpdatedAt ?? 0),
    0
  )
  const ageMs = newest > 0 ? now - newest : 0
  const queryCount = active.length
  if (
    !shouldRefetchStaleVisits(
      visible,
      newest,
      now,
      lastRefetchAt,
      staleMs,
      throttleMs
    )
  ) {
    return { refetched: false, ageMs, queryCount }
  }
  onRefetch?.()
  await queryClient.refetchQueries({
    queryKey: visitScopeKey(),
    type: 'active',
  })
  return { refetched: true, ageMs, queryCount }
}

/**
 * App-level self-heal that keeps the visit-list queries fresh across every
 * calendar view (route browser, personal plan, …) regardless of per-view query
 * options.
 *
 * On mobile the global QueryClient pins `staleTime: Infinity` with no
 * `refetchInterval`/`refetchOnWindowFocus`, so react-query's built-in
 * focus/reconnect refetch never fires (it only re-runs stale queries) and a view
 * that adds no overrides — notably the route browser's per-route `ServiceVisit`
 * lists — never refreshes after a freeze/resume. The data drifts stale while
 * react-query is online, focused and unpaused (field report v1.11.16). This hook
 * closes that gap centrally: on a steady interval and on every resume/focus
 * event, while visible, it force-refetches the active visit-list queries if the
 * freshest one is stale. It supersedes the EmployeeCalendar-specific self-heal.
 *
 * Deliberately separate from the observation-only `useUpdateHealthMonitor` and
 * complementary to `useOnlineRecovery` (which restores connectivity; this
 * refreshes data once connectivity is healthy). Mount once in the authenticated
 * shell.
 */
export const useVisitSelfHeal = () => {
  const queryClient = useQueryClient()
  const serverLog = useServerLog()
  const serverLogRef = useRef(serverLog)
  // Keep the ref current without mutating it during render, so the long-lived
  // interval/listeners below need not be torn down when serverLog changes.
  useEffect(() => {
    serverLogRef.current = serverLog
  }, [serverLog])
  const lastRefetchRef = useRef(0)
  const lastLogRef = useRef(0)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const heal = () => {
      const now = Date.now()
      void healStaleVisits(
        queryClient,
        {
          visible: currentVisibility() === 'visible',
          now,
          lastRefetchAt: lastRefetchRef.current,
          staleMs: STALE_MS,
          throttleMs: REFETCH_THROTTLE_MS,
        },
        // Mark the throttle at issue time, so a slow in-flight refetch does not
        // let the next tick/resume-event re-issue an overlapping one.
        () => {
          lastRefetchRef.current = now
        }
      )
        .then(({ refetched, ageMs, queryCount }) => {
          if (!refetched) {
            return
          }
          // Throttled telemetry so we can see the self-heal engaging in the field
          // (carrying the stale age + query count it acted on, mirroring the old
          // EmployeeCalendar "Visit data is too old (Ns)" breadcrumb) without
          // flooding the log on a view that depends on it for liveness.
          if (now - lastLogRef.current >= 60_000) {
            lastLogRef.current = now
            try {
              serverLogRef.current.warn(
                `[visit-self-heal] refetched ${queryCount} stale visit ${
                  queryCount === 1 ? 'query' : 'queries'
                } (data ${Math.round(ageMs / 1000)}s old)`
              )
            } catch {
              // Never let diagnostics logging throw into the app.
            }
          }
        })
        .catch(() => {
          // A background self-heal must never surface as an unhandled rejection:
          // a failed refetch (transient network, an in-flight query erroring) is
          // swallowed here and the next interval tick / resume event retries.
        })
    }

    const id = setInterval(heal, SAMPLE_INTERVAL_MS)
    DOCUMENT_EVENTS.forEach(type =>
      document.addEventListener(type, heal, { passive: true })
    )
    WINDOW_EVENTS.forEach(type =>
      window.addEventListener(type, heal, { passive: true })
    )
    return () => {
      clearInterval(id)
      DOCUMENT_EVENTS.forEach(type => document.removeEventListener(type, heal))
      WINDOW_EVENTS.forEach(type => window.removeEventListener(type, heal))
    }
    // Closes over stable refs + queryClient only.
  }, [queryClient])
}
